// Country-aware bank-form configuration for Wise payouts.
// Returns the labels, validation rules, and required flags appropriate for
// each country's local bank-transfer format.
//
// Used by: pages/settings/payout.js (web), src/screens/settings/PayoutSettingsScreen.tsx
// (mobile — see src/utils/bankFormConfig.ts for the mobile copy).

function normalizeCountry(country) {
  return String(country || '').trim().toLowerCase();
}

function getBankFormConfig(country) {
  const c = normalizeCountry(country);

  // United States — Wise sends USD via ACH; needs 9-digit ABA routing + account type
  if (c === 'united states' || c === 'us' || c === 'usa') {
    return {
      country: 'US',
      branchCodeLabel: 'ABA Routing Number',
      branchCodeRequired: true,
      branchCodePlaceholder: '9 digits — e.g., 121000358',
      branchCodeMaxLength: 9,
      branchCodeRegex: /^\d{9}$/,
      branchCodeErrorMsg: 'Routing number must be exactly 9 digits',
      branchCodeHelp: 'Find this 9-digit number on the bottom-left of a check, between the special symbols.',
      requiresAccountType: true,
      accountTypeLabel: 'Account Type',
      accountTypeOptions: [
        { value: 'CHECKING', label: 'Checking' },
        { value: 'SAVINGS', label: 'Savings' },
      ],
      accountNumberPlaceholder: 'Your bank account number',
      swiftRequired: false,
      swiftHelp: 'Not needed for US ACH transfers (only for international wires).',
    };
  }

  // United Kingdom — Sort Code + Account Number
  if (c === 'united kingdom' || c === 'uk' || c === 'gb' || c === 'great britain') {
    return {
      country: 'UK',
      branchCodeLabel: 'Sort Code',
      branchCodeRequired: true,
      branchCodePlaceholder: '6 digits — e.g., 04-00-04',
      branchCodeMaxLength: 8,
      branchCodeRegex: /^\d{6}$|^\d{2}-\d{2}-\d{2}$/,
      branchCodeErrorMsg: 'Sort code must be 6 digits',
      branchCodeHelp: 'Six-digit sort code from your bank.',
      requiresAccountType: false,
      accountNumberPlaceholder: '8-digit account number',
      swiftRequired: false,
    };
  }

  // Australia — BSB Code
  if (c === 'australia' || c === 'au') {
    return {
      country: 'AU',
      branchCodeLabel: 'BSB Code',
      branchCodeRequired: true,
      branchCodePlaceholder: '6 digits — e.g., 062-000',
      branchCodeMaxLength: 7,
      branchCodeRegex: /^\d{6}$|^\d{3}-\d{3}$/,
      branchCodeErrorMsg: 'BSB must be exactly 6 digits',
      branchCodeHelp: 'Bank-State-Branch number, 6 digits.',
      requiresAccountType: false,
      accountNumberPlaceholder: 'Your account number',
      swiftRequired: false,
    };
  }

  // Canada — Transit (5) + Institution (3) numbers
  if (c === 'canada' || c === 'ca') {
    return {
      country: 'CA',
      branchCodeLabel: 'Transit + Institution Number',
      branchCodeRequired: true,
      branchCodePlaceholder: '8 digits — 5-digit transit + 3-digit institution',
      branchCodeMaxLength: 9,
      branchCodeRegex: /^\d{8}$|^\d{5}-?\d{3}$/,
      branchCodeErrorMsg: 'Must be 8 digits (5 transit + 3 institution)',
      branchCodeHelp: 'Found on a personal cheque or bank online portal.',
      requiresAccountType: true,
      accountTypeLabel: 'Account Type',
      accountTypeOptions: [
        { value: 'CHECKING', label: 'Chequing' },
        { value: 'SAVINGS', label: 'Savings' },
      ],
      accountNumberPlaceholder: 'Your account number',
      swiftRequired: false,
    };
  }

  // Singapore — Bank Code
  if (c === 'singapore' || c === 'sg') {
    return {
      country: 'SG',
      branchCodeLabel: 'Bank Code',
      branchCodeRequired: true,
      branchCodePlaceholder: '5–7 digits',
      branchCodeMaxLength: 7,
      branchCodeRegex: /^\d{5,7}$/,
      branchCodeErrorMsg: 'Bank code must be 5–7 digits',
      branchCodeHelp: 'Singapore bank-branch code.',
      requiresAccountType: false,
      accountNumberPlaceholder: 'Your account number',
      swiftRequired: false,
    };
  }

  // Default: free-text optional sort code (Ghana, Kenya, Indonesia, etc.)
  return {
    country: 'OTHER',
    branchCodeLabel: 'Branch / Sort Code (optional)',
    branchCodeRequired: false,
    branchCodePlaceholder: 'e.g., 020',
    branchCodeMaxLength: 20,
    branchCodeRegex: /^[\w\- ]*$/,
    branchCodeErrorMsg: '',
    branchCodeHelp: '',
    requiresAccountType: false,
    accountNumberPlaceholder: 'Local bank account number',
    swiftRequired: false,
    swiftHelp: 'Helps speed up international transfers (optional).',
  };
}

module.exports = { getBankFormConfig };
module.exports.default = getBankFormConfig;
